import { ErrorLog } from './ErrorLog';

export type NotificationSocketListener = (_data: any) => void;

const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30000;
// Caps the exponent only -- retries continue indefinitely at RECONNECT_MAX_DELAY.
const RECONNECT_ATTEMPT_CEILING = 5;
// A socket that has never once opened is probably pointed at a misrouted or
// undeployed endpoint. Give up after a few tries instead of piling up a dead
// connection attempt every 30s for the life of the page.
const INITIAL_CONNECT_MAX_ATTEMPTS = 4;

// The server sends a keepalive every 30s. If we go this long with no frame at
// all the connection is dead even if the browser never fired onclose, which is
// what happens when a proxy silently drops it.
const HEARTBEAT_TIMEOUT = 75000;
const HEARTBEAT_CHECK_INTERVAL = 15000;

// Base up to but excluding /ws/notifications -- includes the root path where
// the deployed backend needs one (e.g. wss://host/hqi-backend), and none for
// the local dummy server.
const SOCKET_BASE_URL =
  process.env.REACT_APP_HQI_NOTIFICATION_WS_URL;

/**
 * Note the segment order: ws/notifications/{projectId}, NOT
 * notifications/ws/{projectId}. The API doc says the latter; verified against
 * hqi-dev, only this order upgrades to 101 -- the other returns 403/404.
 *
 * The server closes with 4401 when user_email is absent, so it is a required
 * part of the url rather than an optional param.
 */
export const buildNotificationSocketUrl = (projectId: string | number, userEmail: string) =>
  `${SOCKET_BASE_URL}/ws/notifications/${projectId}` +
  `?user_email=${encodeURIComponent(userEmail)}`;

/** Sent by the server when user_email is missing. Retrying cannot fix it. */
export const WS_CLOSE_MISSING_USER_EMAIL = 4401;

/**
 * Single shared notification socket, scoped to one workspace at a time.
 *
 * Nothing connects on import -- callers own the lifecycle via connect()/disconnect(),
 * so the socket only opens once the user is authenticated and a workspace is active.
 */
class NotificationSocketManager {
  private socket: WebSocket | null = null;
  private url = '';
  private manuallyClosed = false;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private listeners: NotificationSocketListener[] = [];
  private lastMessageAt = 0;
  private watchdogTimer: number | null = null;
  private hasEverConnected = false;

  connect(url: string) {
    // Already pointed at this url and alive: nothing to do.
    if (this.socket && this.url === url) {
      const { readyState } = this.socket;
      if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) return;
    }

    // Switching workspaces: drop the previous socket before opening a new one,
    // otherwise it stays open and its onclose reconnects to the new url,
    // leaving two live sockets for one workspace.
    this.teardownSocket();

    this.url = url;
    this.manuallyClosed = false;
    this.reconnectAttempts = 0;
    this.hasEverConnected = false;
    this.open();
  }

  disconnect() {
    this.manuallyClosed = true;
    this.clearReconnectTimer();
    this.stopWatchdog();
    this.teardownSocket();
    this.url = '';
  }

  /** Returns an unsubscribe function. */
  subscribe(listener: NotificationSocketListener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((item) => item !== listener);
    };
  }

  private teardownSocket() {
    const socket = this.socket;
    if (!socket) return;

    // Detach first so the close we trigger here cannot schedule a reconnect
    // for a url we are abandoning.
    this.socket = null;
    socket.onmessage = null;
    socket.onclose = null;
    socket.onerror = null;

    if (socket.readyState === WebSocket.CONNECTING) {
      // close() mid-handshake makes the browser log "WebSocket is closed
      // before the connection is established" and leaves a dangling attempt.
      // Let the handshake finish, then close it immediately.
      socket.onopen = () => {
        try {
          socket.close();
        } catch (error) {
          ErrorLog('NotificationSocket deferred close', error);
        }
      };
      return;
    }

    socket.onopen = null;
    try {
      socket.close();
    } catch (error) {
      ErrorLog('NotificationSocket close', error);
    }
  }

  private open() {
    if (!this.url) return;

    try {
      this.socket = new WebSocket(this.url);
    } catch (error) {
      ErrorLog('NotificationSocket open', error);
      this.scheduleReconnect();
      return;
    }

    this.lastMessageAt = Date.now();
    this.startWatchdog();

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.hasEverConnected = true;
      this.lastMessageAt = Date.now();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      // Any frame proves the link is alive, heartbeat included.
      this.lastMessageAt = Date.now();

      let data: any;
      try {
        data = JSON.parse(event.data);
      } catch (error) {
        // Server can send plain strings; nothing to hand on.
        return;
      }
      this.emit(data);
    };

    this.socket.onclose = (event: CloseEvent) => {
      this.socket = null;
      this.stopWatchdog();

      if (event.code === WS_CLOSE_MISSING_USER_EMAIL) {
        // Configuration problem, not a transient one -- a retry loop would
        // hammer the server and never succeed.
        ErrorLog('NotificationSocket', `rejected: ${event.reason || 'missing user_email'}`);
        this.manuallyClosed = true;
        return;
      }
      if (!this.manuallyClosed) this.scheduleReconnect();
    };

    this.socket.onerror = (error) => {
      ErrorLog('NotificationSocket error', error);
    };
  }

  private emit(data: any) {
    // Copy first: a listener may unsubscribe while we're iterating.
    this.listeners.slice().forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        ErrorLog('NotificationSocket listener', error);
      }
    });
  }

  private startWatchdog() {
    this.stopWatchdog();
    this.watchdogTimer = window.setInterval(() => {
      if (this.manuallyClosed || !this.socket) return;
      if (Date.now() - this.lastMessageAt < HEARTBEAT_TIMEOUT) return;

      // Dead but still "open": force it closed and let the backoff reconnect.
      ErrorLog('NotificationSocket', 'heartbeat timeout, reconnecting');
      this.teardownSocket();
      this.stopWatchdog();
      this.scheduleReconnect();
    }, HEARTBEAT_CHECK_INTERVAL);
  }

  private stopWatchdog() {
    if (this.watchdogTimer !== null) {
      window.clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  private scheduleReconnect() {
    if (this.manuallyClosed || this.reconnectTimer !== null) return;

    // Distinguish "was working, dropped" from "never worked at all". A
    // transient drop deserves indefinite retries; an endpoint that has never
    // completed a handshake is a routing/deploy problem a retry cannot fix.
    if (!this.hasEverConnected && this.reconnectAttempts >= INITIAL_CONNECT_MAX_ATTEMPTS) {
      ErrorLog(
        'NotificationSocket',
        `giving up after ${this.reconnectAttempts} failed attempts to ${this.url}`,
      );
      this.manuallyClosed = true;
      return;
    }

    // Back off so a dead server isn't hammered once a second forever. Once a
    // connection has succeeded, retry indefinitely so the server coming back
    // recovers the stream without a page reload.
    const delay = Math.min(
      RECONNECT_BASE_DELAY * Math.pow(2, this.reconnectAttempts),
      RECONNECT_MAX_DELAY,
    );
    this.reconnectAttempts = Math.min(this.reconnectAttempts + 1, RECONNECT_ATTEMPT_CEILING);

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

const notificationSocket = new NotificationSocketManager();
export default notificationSocket;
